import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";

const EMPTY_ARRAY = [];

const getSupplierId = (supplier) => String(supplier?.supplierId || supplier?._id || "");

const getSupplierInitials = (vendorName = "") =>
  vendorName
    .split(" ")
    .filter(Boolean)
    .slice(0, 4)
    .map((namePart) => namePart.charAt(0).toUpperCase())
    .join("");

const getSupplierRank = (supplier) =>
  supplier?.vendorQuotations?.rank ||
  supplier?.vendorQuotations?.rankWithGst ||
  supplier?.quotationDetails?.rank ||
  "NA";

const getRankNumber = (rank = "") => {
  const parsedRank = parseInt(String(rank).replace("L", ""), 10);
  return Number.isNaN(parsedRank) ? Number.POSITIVE_INFINITY : parsedRank;
};

const getSavedSupplierIds = ({ displayRanks, vendorOptions }) => {
  const hasSupplierLevelRankVisibility = vendorOptions.some((supplier) => typeof supplier.canViewRank === "boolean");
  if (hasSupplierLevelRankVisibility) {
    return vendorOptions.filter((supplier) => supplier.canViewRank).map((supplier) => supplier.supplierId);
  }

  return displayRanks ? vendorOptions.map((supplier) => supplier.supplierId) : [];
};
const DisplayRankVendorsDialog = ({
  open,
  suppliers = EMPTY_ARRAY,
  displayRanks = false,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const vendorOptions = useMemo(
    () =>
      [...(suppliers || [])]
        .filter((supplier) => supplier?.quotationReceived)
        .map((supplier) => ({
          supplierId: getSupplierId(supplier),
          businessName: supplier?.businessName || "Unnamed Vendor",
          rank: getSupplierRank(supplier),
          initials: getSupplierInitials(supplier?.businessName),
          canViewRank: supplier?.canViewRank,
        }))
        .filter((supplier) => supplier.supplierId && supplier.rank && supplier.rank !== "NA")
        .sort((firstSupplier, secondSupplier) => getRankNumber(firstSupplier.rank) - getRankNumber(secondSupplier.rank)),
    [suppliers]
  );

  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const vendorOptionsKey = vendorOptions.map((supplier) => supplier.supplierId).join("|");

  useEffect(() => {
    if (!open) return;

    setSelectedSupplierIds(getSavedSupplierIds({ displayRanks, vendorOptions }));
  }, [displayRanks, open, vendorOptionsKey]);

  if (!open) return null;

  const selectedSupplierIdSet = new Set(selectedSupplierIds);
  const areAllSelected =
    vendorOptions.length > 0 &&
    vendorOptions.every((supplier) => selectedSupplierIdSet.has(supplier.supplierId));

  const handleToggleVendor = (supplierId) => {
    setSelectedSupplierIds((prev) => {
      const nextSelectedSupplierIds = new Set(prev);
      if (nextSelectedSupplierIds.has(supplierId)) {
        nextSelectedSupplierIds.delete(supplierId);
      } else {
        nextSelectedSupplierIds.add(supplierId);
      }
      return Array.from(nextSelectedSupplierIds);
    });
  };

  const handleToggleAll = () => {
    setSelectedSupplierIds(areAllSelected ? [] : vendorOptions.map((supplier) => supplier.supplierId));
  };
  const handleSubmit = () => {
    onSubmit?.(selectedSupplierIds);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="flex max-h-[calc(100vh-48px)] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <LeaderboardOutlinedIcon className="text-[#1F2937]" style={{ fontSize: 24 }} />
            <div>
              <h3 className="text-base font-semibold text-[#1F2937]">Show Rank</h3>
              <p className="mt-1 text-xs text-[#6B7280]">Select quoted vendors who can see their rank.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close show rank popup"
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto px-6 py-4">
          {vendorOptions.length === 0 ? (
            <div className="rounded border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
              No ranked quotations are available yet.
            </div>
          ) : (
            <div className="overflow-hidden rounded border border-gray-300">
              <div className="border-b border-gray-300 bg-gray-50 text-xs font-semibold text-[#111827]">
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={areAllSelected}
                    onChange={handleToggleAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-700"
                  />
                  <span>Vendor Name</span>
                </label>
              </div>

              {vendorOptions.map((supplier) => (
                <label
                  key={supplier.supplierId}
                  className="flex min-w-0 cursor-pointer items-center gap-3 border-b border-gray-200 px-3 py-2 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedSupplierIdSet.has(supplier.supplierId)}
                    onChange={() => handleToggleVendor(supplier.supplierId)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-700"
                  />
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#343417] text-[10px] text-white">
                    {supplier.initials}
                  </div>
                  <p className="min-w-0 truncate text-xs text-[#111827]">
                    {supplier.businessName} ({supplier.rank})
                  </p>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="min-w-[104px] rounded-sm border border-[#6B7280] px-4 py-2 text-xs text-[#111827] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="min-w-[104px] rounded-sm bg-[#181622] px-4 py-2 text-xs text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisplayRankVendorsDialog;
